import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { AppData, Invoice, IInvoiceAnnotations, VatExemptionType } from 'fakturnia-shared';
import { Subject } from 'rxjs';

/**
 * Definicja adnotacji dla UI
 */
interface AnnotationDefinition {
  id: string;
  field: string;
  label: string;
  shortLabel: string;
  description: string;
  excludes: string[];
  hasConfig: boolean;
}

/**
 * Komponent karty adnotacji dokumentu
 * 
 * Pozwala użytkownikowi zaznaczać adnotacje na fakturze:
 * - Mechanizm podzielonej płatności (MPP)
 * - Odwrotne obciążenie
 * - Metoda kasowa
 * - Samofakturowanie
 * - Procedura marży
 * - Zwolnienie z VAT (zgodnie z KSeF: typ A/B/C + tekst podstawy)
 * 
 * Waliduje wykluczające się kombinacje (np. MPP + metoda kasowa)
 */
@Component({
  selector: 'app-annotations-card',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatIconModule,
    MatTooltipModule,
    MatSlideToggleModule
  ],
  templateUrl: './annotations-card.component.html',
  styleUrls: ['./annotations-card.component.scss']
})
export class AnnotationsCardComponent implements OnInit, OnDestroy, OnChanges {
  private _destroy$ = new Subject<void>();

  @Input() invoice!: Invoice;
  @Input() hasZwVatRate: boolean = false; // Czy faktura ma pozycje ze stawką "zw"
  @Input() bankAccounts: any[] = []; // Lista kont bankowych dla konta VAT (MPP)
  // Domyślne wartości zwolnienia z VAT z ustawień użytkownika — używane do
  // ponownego wczytania po włączeniu adnotacji (gdy pola są puste).
  @Input() vatExemptionDefaults: { exemptionType?: VatExemptionType | null; legalBasis?: string | null; showOnDocument?: boolean } | null = null;
  
  @Output() annotationsChange = new EventEmitter<IInvoiceAnnotations>();
  @Output() renderPreview = new EventEmitter<void>();
  @Output() selfInvoicingChange = new EventEmitter<boolean>(); // Informuje o zmianie samofakturowania
  @Output() splitPaymentChange = new EventEmitter<boolean>(); // Informuje o zmianie MPP - do auto-wyboru konta VAT

  // Stan rozwinięcia panelu
  isExpanded = false;

  // Dane statyczne
  marginProcedures = AppData.MarginProcedures;
  vatExemptionTypes = AppData.VatExemptionTypes;
  /** @deprecated Zachowane dla kompatybilności wstecznej */
  vatExemptionBases = AppData.VatExemptionBases;
  
  // Definicje adnotacji (lokalnie, bo AppData.InvoiceAnnotations może nie istnieć)
  annotationDefinitions: AnnotationDefinition[] = [
    {
      id: 'splitPayment',
      field: 'isSplitPayment',
      label: 'Mechanizm podzielonej płatności',
      shortLabel: 'MPP',
      description: 'Nabywca płaci VAT na specjalne konto VAT sprzedawcy (split payment)',
      excludes: ['cashMethod'],
      hasConfig: false
    },
    {
      id: 'reverseCharge',
      field: 'isReverseCharge',
      label: 'Odwrotne obciążenie',
      shortLabel: 'OO',
      description: 'Nabywca sam rozlicza VAT (reverse charge)',
      excludes: [],
      hasConfig: false
    },
    {
      id: 'cashMethod',
      field: 'isCashMethod',
      label: 'Metoda kasowa',
      shortLabel: 'Kasowa',
      description: 'VAT rozliczany w momencie otrzymania zapłaty',
      excludes: ['splitPayment'],
      hasConfig: false
    },
    {
      id: 'selfInvoicing',
      field: 'isSelfInvoicing',
      label: 'Samofakturowanie',
      shortLabel: 'Samofakturowanie',
      description: 'Faktura wystawiona przez nabywcę w imieniu sprzedawcy',
      excludes: [],
      hasConfig: false
    }
  ];

  // Stan UI dla zwolnienia z VAT (nowy model KSeF)
  selectedExemptionType: VatExemptionType | null = null;
  exemptionLegalBasis: string = '';

  // Komunikaty walidacji
  validationErrors: string[] = [];

  // Flaga czy już zainicjalizowano z invoice
  private _initialized = false;

  ngOnInit(): void {
    this._initializeFromInvoice();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reinicjalizuj gdy invoice się zmieni (np. przy ładowaniu dokumentu do edycji)
    if (changes['invoice'] && changes['invoice'].currentValue) {
      this._initializeFromInvoice();
    }
  }

  /**
   * Publiczna metoda do odświeżenia stanu komponentu z aktualnych danych invoice.
   * Używana gdy zewnętrznie zmieniane są wartości invoice.annotations bez zmiany referencji.
   */
  public refreshFromInvoice(): void {
    this._initializeFromInvoice();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  /**
   * Getter dla annotations z bracket notation (dla tsconfig noPropertyAccessFromIndexSignature)
   */
  private get _annotations(): IInvoiceAnnotations {
    return this.invoice?.['annotations'] || {};
  }

  /**
   * Setter dla annotations
   * Używamy Object.assign aby zachować instancję klasy InvoiceAnnotations jeśli istnieje
   */
  private _setAnnotations(ann: IInvoiceAnnotations): void {
    if (this.invoice['annotations']) {
      Object.assign(this.invoice['annotations'], ann);
    } else {
      this.invoice['annotations'] = ann as any;
    }
  }

  /**
   * Inicjalizuje stan komponentu na podstawie danych z invoice
   */
  private _initializeFromInvoice(): void {
    const ann = this._annotations;
    if (!ann) return;

    // Automatycznie rozwiń panel jeśli są aktywne adnotacje lub wymagane zwolnienie VAT
    this.isExpanded = this._hasAnyActiveAnnotation() || this.hasZwVatRate;

    // Zwolnienie z VAT - obsługa nowego modelu KSeF
    // Obsługuj zarówno enabled=true jak i gdy mamy exemptionType/legalBasis (domyślne ustawienia)
    if (ann.vatExemption?.enabled || ann.vatExemption?.exemptionType || ann.vatExemption?.legalBasis) {
      // Nowy model: exemptionType + legalBasis
      if (ann.vatExemption.exemptionType) {
        this.selectedExemptionType = ann.vatExemption.exemptionType;
      }
      if (ann.vatExemption.legalBasis) {
        this.exemptionLegalBasis = ann.vatExemption.legalBasis;
      }
      
      // Kompatybilność wsteczna: stary model baseId/customText
      if (!ann.vatExemption.exemptionType && ann.vatExemption.isCustom) {
        // Stary custom text → typ C (inna podstawa)
        this.selectedExemptionType = 'C';
        this.exemptionLegalBasis = ann.vatExemption.customText || '';
      } else if (!ann.vatExemption.exemptionType && ann.vatExemption.baseId) {
        // Stary baseId → mapowanie na typ A lub B
        const oldBase = this.vatExemptionBases.find(b => b.id === ann.vatExemption?.baseId);
        if (oldBase) {
          this.selectedExemptionType = oldBase.ksefField === 'P_19B' ? 'B' : 'A';
          this.exemptionLegalBasis = oldBase.ksefValue || oldBase.text;
        }
      }
    }

    this._validateCombinations();
  }

  /**
   * Sprawdza czy adnotacja jest zablokowana przez inną
   */
  isAnnotationDisabled(annotationId: string): boolean {
    const ann = this._annotations;
    if (!ann) return false;

    // MPP blokuje metodę kasową i odwrotnie
    if (annotationId === 'splitPayment' && ann.isCashMethod) return true;
    if (annotationId === 'cashMethod' && ann.isSplitPayment) return true;

    return false;
  }

  /**
   * Zwraca tooltip dla zablokowanej adnotacji
   */
  getDisabledTooltip(annotationId: string): string {
    const ann = this._annotations;
    if (annotationId === 'splitPayment' && ann?.isCashMethod) {
      return 'Mechanizm podzielonej płatności wyklucza się z metodą kasową';
    }
    if (annotationId === 'cashMethod' && ann?.isSplitPayment) {
      return 'Metoda kasowa wyklucza się z mechanizmem podzielonej płatności';
    }
    return '';
  }

  /**
   * Pobiera wartość boolean adnotacji
   */
  getAnnotationValue(field: string): boolean {
    const ann = this._annotations;
    return ann?.[field as keyof IInvoiceAnnotations] as boolean || false;
  }

  /**
   * Obsługa zmiany checkboxa prostej adnotacji (boolean)
   */
  onSimpleAnnotationChange(field: 'isSplitPayment' | 'isReverseCharge' | 'isCashMethod' | 'isSelfInvoicing', value: boolean): void {
    const ann = this._annotations || {};
    ann[field] = value;
    this._setAnnotations(ann);

    this._validateCombinations();
    this._emitChanges();
    
    // Specjalna obsługa dla samofakturowania
    if (field === 'isSelfInvoicing') {
      this.selfInvoicingChange.emit(value);
    }
    
    // Specjalna obsługa dla MPP - auto-wybór konta VAT
    if (field === 'isSplitPayment') {
      this.splitPaymentChange.emit(value);
    }
  }

  /**
   * Obsługa włączenia/wyłączenia procedury marży
   */
  onMarginProcedureToggle(enabled: boolean): void {
    const ann = this._annotations || {};

    if (enabled) {
      ann.marginProcedure = {
        enabled: true,
        procedureId: 1 // Domyślnie "biura podróży"
      };
      // Synchronizuj z legacy polem
      this.invoice['marginProcedureId'] = 1;
    } else {
      ann.marginProcedure = null;
      this.invoice['marginProcedureId'] = null;
    }

    this._setAnnotations(ann);
    this._validateCombinations();
    this._emitChanges();
  }

  /**
   * Obsługa wyboru typu procedury marży
   */
  onMarginProcedureChange(procedureId: number): void {
    const ann = this._annotations;
    if (!ann?.marginProcedure) return;

    ann.marginProcedure.procedureId = procedureId;
    this._setAnnotations(ann);
    // Synchronizuj z legacy polem
    this.invoice['marginProcedureId'] = procedureId;
    
    this._emitChanges();
  }

  /**
   * Obsługa włączenia/wyłączenia zwolnienia z VAT
   */
  onVatExemptionToggle(enabled: boolean): void {
    const ann = this._annotations || {};

    if (enabled) {
      const existing = ann.vatExemption;
      const d = this.vatExemptionDefaults;
      // Wczytaj wartości: istniejące → zachowane w komponencie → domyślne z ustawień
      const exemptionType = existing?.exemptionType || this.selectedExemptionType || d?.exemptionType || null;
      const legalBasis = existing?.legalBasis || existing?.customText || this.exemptionLegalBasis || d?.legalBasis || null;
      const showOnDocument = existing?.showOnDocument ?? (d?.showOnDocument ?? false);

      // Nowy model KSeF
      ann.vatExemption = {
        enabled: true,
        exemptionType,
        legalBasis,
        showOnDocument,
        // Legacy pola dla kompatybilności
        baseId: existing?.baseId || null,
        customText: legalBasis,
        isCustom: true // W nowym modelu zawsze custom text
      };

      this.selectedExemptionType = exemptionType;
      this.exemptionLegalBasis = legalBasis || '';
      this.invoice['vatExemptionBase'] = legalBasis;
      this.invoice['vatExemptionBaseCustom'] = !!legalBasis;
      this.invoice['showVatExemptionBase'] = showOnDocument;
    } else {
      ann.vatExemption = null;
      // NIE czyść selectedExemptionType / exemptionLegalBasis — zachowujemy je,
      // by po ponownym włączeniu adnotacji wartości się przywróciły (a gdy puste,
      // wczytają się z domyślnych ustawień). Czyścimy tylko pola legacy na fakturze.
      this.invoice['vatExemptionBase'] = null;
      this.invoice['vatExemptionBaseCustom'] = false;
      this.invoice['showVatExemptionBase'] = false;
    }

    this._setAnnotations(ann);
    this._validateCombinations();
    this._emitChanges();
  }

  /**
   * Obsługa wyboru typu podstawy prawnej zwolnienia (A/B/C)
   */
  onExemptionTypeChange(type: VatExemptionType | null): void {
    const ann = this._annotations;
    if (!ann?.vatExemption) return;

    this.selectedExemptionType = type;
    ann.vatExemption.exemptionType = type;
    
    // Synchronizuj z legacy polami
    ann.vatExemption.isCustom = true;
    
    this._setAnnotations(ann);
    this._validateCombinations();
    this._emitChanges();
  }

  /**
   * Obsługa wpisania podstawy prawnej
   */
  onLegalBasisBlur(): void {
    const ann = this._annotations;
    if (!ann?.vatExemption) return;

    ann.vatExemption.legalBasis = this.exemptionLegalBasis;
    // Synchronizuj z legacy polami
    ann.vatExemption.customText = this.exemptionLegalBasis;
    this.invoice['vatExemptionBase'] = this.exemptionLegalBasis;
    
    this._setAnnotations(ann);
    this._validateCombinations();
    this._emitChanges();
  }

  onLegalBasisChange(value: string): void {
    this.exemptionLegalBasis = value;
    const ann = this._annotations;
    if (!ann?.vatExemption) return;

    ann.vatExemption.legalBasis = value;
    ann.vatExemption.customText = value;
    this.invoice['vatExemptionBase'] = value;

    this._setAnnotations(ann);
    this._emitChanges();
  }

  /**
   * Obsługa toggle "Pokaż na dokumencie" dla zwolnienia VAT
   */
  onShowVatExemptionChange(show: boolean): void {
    const ann = this._annotations;
    if (!ann?.vatExemption) return;

    ann.vatExemption.showOnDocument = show;
    this._setAnnotations(ann);
    // Synchronizuj z legacy
    this.invoice['showVatExemptionBase'] = show;
    
    this._emitChanges();
  }

  /**
   * Zwraca placeholder dla pola tekstowego na podstawie wybranego typu
   */
  getExemptionPlaceholder(): string {
    if (!this.selectedExemptionType) return 'np. art. 43 ust. 1 pkt 19 ustawy o VAT';
    const typeConfig = this.vatExemptionTypes.find(t => t.type === this.selectedExemptionType);
    return typeConfig?.placeholder || 'np. art. 43 ust. 1 pkt 19 ustawy o VAT';
  }

  /**
   * Waliduje kombinacje adnotacji
   */
  private _validateCombinations(): void {
    this.validationErrors = [];
    const ann = this._annotations;
    if (!ann) return;

    // MPP + metoda kasowa
    if (ann.isSplitPayment && ann.isCashMethod) {
      this.validationErrors.push('Mechanizm podzielonej płatności wyklucza się z metodą kasową');
    }

    // Procedura marży wymaga wybranej procedury
    if (ann.marginProcedure?.enabled && !ann.marginProcedure.procedureId) {
      this.validationErrors.push('Wybierz rodzaj procedury marży');
    }

    // Zwolnienie VAT - walidacja opcjonalna, nie wymagamy wybrania podstawy
    // Podstawa może być ustawiona automatycznie z domyślnych ustawień użytkownika
    // if (ann.vatExemption?.enabled) {
    //   if (!ann.vatExemption.exemptionType) {
    //     this.validationErrors.push('Wybierz typ podstawy prawnej zwolnienia z VAT');
    //   }
    //   if (!ann.vatExemption.legalBasis && !ann.vatExemption.customText) {
    //     this.validationErrors.push('Podaj treść podstawy prawnej zwolnienia z VAT');
    //   }
    // }
  }

  /**
   * Emituje zmiany do rodzica
   */
  private _emitChanges(): void {
    this.annotationsChange.emit(this._annotations);
    this.renderPreview.emit();
  }

  /**
   * Sprawdza czy procedura marży jest włączona
   */
  get isMarginProcedureEnabled(): boolean {
    return this._annotations?.marginProcedure?.enabled || false;
  }

  /**
   * Sprawdza czy zwolnienie VAT jest włączone
   */
  get isVatExemptionEnabled(): boolean {
    return this._annotations?.vatExemption?.enabled || false;
  }

  /**
   * Walidacja wizualna (NIE blokuje wysłania) — przy włączonym zwolnieniu z VAT
   * typ podstawy prawnej oraz treść podstawy są wymagane.
   */
  get isExemptionTypeMissing(): boolean {
    return this.isVatExemptionEnabled && !this.selectedExemptionType;
  }

  get isExemptionBasisMissing(): boolean {
    return this.isVatExemptionEnabled && !(this.exemptionLegalBasis || '').trim();
  }

  /**
   * Pobiera aktualny ID procedury marży
   */
  get currentMarginProcedureId(): number | null {
    return this._annotations?.marginProcedure?.procedureId || null;
  }

  /**
   * Sprawdza czy pokazywać ostrzeżenie o wymaganym zwolnieniu VAT
   */
  get showVatExemptionWarning(): boolean {
    return this.hasZwVatRate && !this.isVatExemptionEnabled;
  }

  /**
   * Pobiera wartość showOnDocument dla zwolnienia VAT
   */
  get vatExemptionShowOnDocument(): boolean {
    return this._annotations?.vatExemption?.showOnDocument || false;
  }

  /**
   * Sprawdza czy jest aktywna jakakolwiek adnotacja
   */
  private _hasAnyActiveAnnotation(): boolean {
    const ann = this._annotations;
    if (!ann) return false;
    
    return !!(
      ann.isSplitPayment ||
      ann.isReverseCharge ||
      ann.isCashMethod ||
      ann.isSelfInvoicing ||
      ann.marginProcedure?.enabled ||
      ann.vatExemption?.enabled
    );
  }

  /**
   * Sprawdza czy MPP jest aktywny (dla wyświetlenia konta VAT)
   */
  get isSplitPaymentEnabled(): boolean {
    return this._annotations?.isSplitPayment || false;
  }

  /**
   * Toggle panelu adnotacji
   */
  toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;
  }

  /**
   * Getter dla liczby aktywnych adnotacji (do wyświetlenia w nagłówku)
   */
  get activeAnnotationsCount(): number {
    const ann = this._annotations;
    if (!ann) return 0;
    
    let count = 0;
    if (ann.isSplitPayment) count++;
    if (ann.isReverseCharge) count++;
    if (ann.isCashMethod) count++;
    if (ann.isSelfInvoicing) count++;
    if (ann.marginProcedure?.enabled) count++;
    if (ann.vatExemption?.enabled) count++;
    
    return count;
  }

  /**
   * Obsługa zmiany konta VAT (dla MPP)
   * Przekazuje pełne dane konta: nazwę banku, SWIFT i opis dla wyświetlania na PDF
   */
  onVatAccountChange(value: string): void {
    const selectedAccount = this.bankAccounts.find((acc: any) => acc.accountNumber === value);
    if (selectedAccount) {
      this.invoice.setVatAccountNumber(
        value,
        selectedAccount.bank,
        selectedAccount.swift,
        selectedAccount.accountDescription
      );
    } else {
      this.invoice.setVatAccountNumber(value);
    }
    this._emitChanges();
  }
}
